import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPerm } from "@/lib/permissions";
import { toast } from "sonner";
import { Headphones, Users, ListChecks, FileSearch, ChevronRight } from "lucide-react";

const STATUSES = ["PENDING", "CONFIRMED", "COMPLETED", "CANCELLED", "CONVERTED"];

const statusColors = {
  PENDING: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  CONFIRMED: "bg-blue-500/15 text-blue-700 border-blue-500/30",
  COMPLETED: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  CANCELLED: "bg-red-500/15 text-red-700 border-red-500/30",
  CONVERTED: "bg-fuchsia-500/15 text-fuchsia-700 border-fuchsia-500/30",
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [trials, setTrials] = useState([]);
  const canManage = hasPerm(user, "trial:manage");

  const load = async () => {
    try {
      const r = await api.get("/trial/list");
      setTrials(r.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/trial/${id}/status`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <DashboardLayout title="Admin · Trial Bookings">
      {/* Admin quick links */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5" data-testid="admin-quick-links">
        {[
          { to: "/app/admin/content", Icon: Headphones, label: "Content Manager", sub: "Audio · Keys · OCR" },
          { to: "/app/admin/listening-catalogue", Icon: Headphones, label: "Listening Catalogue", sub: "50+ practice tests" },
          { to: "/app/admin/leads",   Icon: Users,      label: "Sales Leads",     sub: "Free Assessment funnel" },
          { to: "/app/admin/users",   Icon: Users,      label: "User Roles",      sub: "Promote / Demote" },
          { to: "/app/admin/payments", Icon: ListChecks, label: "Payments",        sub: "EasyPaisa · Manual review" },
          { to: "/app/finance",       Icon: ListChecks, label: "Finance Summary", sub: "Revenue · Trials" },
        ].map((q) => (
          <Link key={q.to} to={q.to} data-testid={`admin-link-${q.label.toLowerCase().replace(/\s/g,'-')}`}
            className="group flex items-center gap-3 px-4 py-3 rounded-sm bg-white border border-slate-200 hover:border-[#F77F00] hover:shadow-md transition-all">
            <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-[#F77F00]/10 text-[#F77F00]">
              <q.Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-sm text-[#003049] leading-none">{q.label}</div>
              <div className="font-mono text-[9px] tracking-[0.25em] uppercase mt-1 text-slate-500">{q.sub}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#F77F00] group-hover:translate-x-0.5 transition-all" />
          </Link>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-3xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display text-xl">Trial bookings</div>
            <div className="text-xs text-slate-400 mt-1">
              {trials.length} total · {trials.filter((t) => t.status === "PENDING").length} pending
            </div>
          </div>
        </div>
        <div className="space-y-2">
          {trials.map((t) => (
            <div key={t.id} data-testid={`trial-${t.id}`}
              className="rounded-xl border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium">{t.full_name}</div>
                <div className="text-xs text-slate-500">{t.email} · {t.phone || "no phone"}</div>
              </div>
              <div className="text-xs text-slate-500">{t.test_type} · target {t.target_score || "—"}</div>
              <div className="text-xs text-slate-400">{t.preferred_date || "Any date"} {t.preferred_time || ""}</div>
              {canManage ? (
                <select data-testid={`trial-status-${t.id}`} value={t.status}
                  onChange={(e) => setStatus(t.id, e.target.value)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${statusColors[t.status] || "border-slate-200"}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <span className={`text-xs px-3 py-1 rounded-full border ${statusColors[t.status] || "border-slate-200"}`}>
                  {t.status}
                </span>
              )}
            </div>
          ))}
          {!trials.length && <div className="text-slate-400 py-8 text-center">No bookings yet.</div>}
        </div>
      </div>
    </DashboardLayout>
  );
}
